import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type RunStatus = 'running' | 'completed' | 'failed' | 'aborted'
export type RunFlow = 'sales' | 'purchases'
export type ProductPolicy = 'generic' | 'per_sku' | 'hybrid'

export interface HistoricalImportRun {
  id: string
  started_at: string
  finished_at: string | null
  duration_seconds: number | null
  flow: RunFlow
  country: string
  dry_run: boolean
  product_policy: ProductPolicy
  status: RunStatus
  total_files: number
  docs_created: number
  docs_skipped: number
  errors_count: number
  customers_created: number
  suppliers_created: number
  products_created: number
  started_by_email: string | null
}

export interface HistoricalImportRunDetail extends HistoricalImportRun {
  source_path: string
  errors_json: Array<{ file?: string; folio?: number; error: string }>
  summary_json: Record<string, unknown>
}

export function useHistoricalRuns(params: { page?: number; pageSize?: number; flow?: RunFlow; status?: RunStatus } = {}) {
  const search = new URLSearchParams()
  if (params.page) search.set('page', String(params.page))
  if (params.pageSize) search.set('page_size', String(params.pageSize))
  if (params.flow) search.set('flow', params.flow)
  if (params.status) search.set('status', params.status)
  const qs = search.toString()
  return useQuery({
    queryKey: ['historical-runs', params],
    queryFn: async () => {
      const res = await apiClient.get(`/historical-import/runs/${qs ? `?${qs}` : ''}`)
      return res.data as { runs: HistoricalImportRun[]; count: number; page: number; page_size: number; pages: number }
    },
  })
}

export function useHistoricalRunDetail(runId: string | null) {
  return useQuery({
    queryKey: ['historical-run', runId],
    queryFn: async () => {
      const res = await apiClient.get(`/historical-import/runs/${runId}/`)
      return res.data as HistoricalImportRunDetail
    },
    enabled: !!runId,
    refetchInterval: (q) => {
      const data = q.state.data as HistoricalImportRunDetail | undefined
      return data?.status === 'running' ? 3000 : false
    },
  })
}

export function useCreateRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { flow: RunFlow; path?: string; upload_id?: string; product_policy?: ProductPolicy; dry_run?: boolean }) => {
      const res = await apiClient.post('/historical-import/runs/', payload)
      return res.data as HistoricalImportRunDetail
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['historical-runs'] }),
  })
}

export interface UploadResult {
  upload_id: string
  path: string
  file_count: number
  total_size_kb: number
  warnings: string[]
}

export function useUploadXmls() {
  return useMutation({
    mutationFn: async (input: { file?: File; files?: File[]; onProgress?: (pct: number) => void }) => {
      const fd = new FormData()
      if (input.file) fd.append('file', input.file)
      if (input.files) input.files.forEach(f => fd.append('files', f))
      const res = await apiClient.post('/historical-import/uploads/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (input.onProgress && e.total) {
            input.onProgress(Math.round((e.loaded * 100) / e.total))
          }
        },
      })
      return res.data as UploadResult
    },
  })
}

export function useRollbackRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (runId: string) => {
      const res = await apiClient.delete(`/historical-import/runs/${runId}/`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['historical-runs'] }),
  })
}
